import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { notAvailableYetHint } from "@/lib/notAvailableYet";
import { formatDateOnly } from "@/lib/dateOnly";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface EntityListProps {
  onEdit: (entity: any) => void;
}

const PAGE_SIZE = 10;
// Entity Type (Vehicle (SPV) / Operating entity) and the merchants each entity
// relates to need UC-219; every entity today is a vehicle.
const ENTITY_TYPE_USE_CASE = "UC-219";

export const EntityList = ({ onEdit }: EntityListProps) => {
  const [entities, setEntities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entityToDelete, setEntityToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchEntities();
  }, []);

  // FIG-324: search and 10 rows at a time, since entities could number in the hundreds.
  const query = search.trim().toLowerCase();
  const filtered = useMemo(
    () => entities.filter((e) => !query || `${e.vehicle_name ?? ""} ${e.vehicle_short_name ?? ""}`.toLowerCase().includes(query)),
    [entities, query],
  );
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  const fetchEntities = async () => {
    try {
      const { data, error } = await supabase
        .from("entities")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setEntities(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!entityToDelete) return;

    setIsDeleting(true);
    try {
      // Delete the file from storage if it exists
      if (entityToDelete.listing_documents_url) {
        const fileName = `${entityToDelete.id}.${entityToDelete.listing_documents_url.split('.').pop()}`;
        await supabase.storage
          .from('listing-documents')
          .remove([fileName]);
      }

      // Delete the entity
      const { error } = await supabase
        .from("entities")
        .delete()
        .eq("id", entityToDelete.id);

      if (error) throw error;

      toast({ title: "Entity deleted successfully" });
      setEntities(entities.filter((e) => e.id !== entityToDelete.id));
      setDeleteDialogOpen(false);
      setEntityToDelete(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (entities.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No entities found. Create one to get started.
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Entity type">
          <span className="text-sm text-muted-foreground">Show</span>
          <Button type="button" variant="secondary" size="sm" aria-pressed="true">Vehicles (SPV) ({entities.length})</Button>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex">
                <Button type="button" variant="outline" size="sm" disabled aria-pressed="false"
                  aria-label={`Operating entities. ${notAvailableYetHint(ENTITY_TYPE_USE_CASE)}`}>
                  Operating entities
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent><p>{notAvailableYetHint(ENTITY_TYPE_USE_CASE)}</p></TooltipContent>
          </Tooltip>
        </div>
        <div className="relative w-64 max-w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} aria-hidden="true" />
          <Input
            type="search"
            aria-label="Search entities"
            placeholder="Search entities…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className="pl-9"
          />
        </div>
      </div>
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Legal Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Short Name</TableHead>
              <TableHead>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="cursor-help" aria-label={`Merchants. ${notAvailableYetHint(ENTITY_TYPE_USE_CASE)}`}>Merchants</span>
                  </TooltipTrigger>
                  <TooltipContent><p>{notAvailableYetHint(ENTITY_TYPE_USE_CASE)}</p></TooltipContent>
                </Tooltip>
              </TableHead>
              <TableHead>Created On</TableHead>
              <TableHead>Documents</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((entity) => (
              <TableRow key={entity.id}>
                <TableCell className="font-medium">
                  {entity.vehicle_name}
                </TableCell>
                <TableCell>Vehicle (SPV)</TableCell>
                <TableCell>{entity.vehicle_short_name || "-"}</TableCell>
                <TableCell className="text-muted-foreground">—</TableCell>
                <TableCell>{formatDateOnly(entity.created_on)}</TableCell>
                <TableCell>
                  {entity.listing_documents_url ? (
                    <a
                      href={entity.listing_documents_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      View
                    </a>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(entity)}
                      aria-label={`Edit ${entity.vehicle_name}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEntityToDelete(entity);
                        setDeleteDialogOpen(true);
                      }}
                      aria-label={`Delete ${entity.vehicle_name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {visible.length === 0 && (
          <div className="py-8 text-center text-sm text-muted-foreground">No entities match your search.</div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3 text-sm text-muted-foreground">
          <span data-testid="entities-footer">
            {filtered.length === 0
              ? "Showing 0 entities"
              : `Showing ${currentPage * PAGE_SIZE + 1}–${currentPage * PAGE_SIZE + visible.length} of ${filtered.length} entities`}
          </span>
          {pageCount > 1 && (
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous</Button>
              <span>Page {currentPage + 1} of {pageCount}</span>
              <Button type="button" variant="outline" size="sm" disabled={currentPage >= pageCount - 1} onClick={() => setPage(currentPage + 1)}>Next</Button>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entity</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{entityToDelete?.vehicle_name}"?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
};
